import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, currency, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Plus, Phone, Mail, Sparkles, Users as UsersIcon, Code2, ExternalLink, Copy } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const emptyCustomer = {
  name: "",
  phone: "",
  email: "",
  desired_ymm: "",
  engine_type: "",
  transmission: "",
  steering: "",
  colors: "",
  time_frame: "",
  payment: "",
  notes: "",
  budget_min: "",
  budget_max: "",
};

const TRANSMISSION_OPTIONS = [
  { value: "manual", label: "Manual" },
  { value: "automatic", label: "Automatic" },
];
const STEERING_OPTIONS = [
  { value: "lhd", label: "Left Hand Drive (If Applicable)" },
  { value: "rhd", label: "Right Hand Drive" },
];
const TIME_FRAME_OPTIONS = [
  { value: "now", label: "Ready to Buy Now" },
  { value: "3_6", label: "3 to 6 months" },
  { value: "6_12", label: "6 to 12 months" },
];
const PAYMENT_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "financing", label: "Financing" },
];

function EmbedPanel() {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const inquireUrl = `${base}/inquire`;
  const iframeCode = `<iframe src="${inquireUrl}" width="100%" height="900" frameborder="0" style="border:0;max-width:720px"></iframe>`;
  const linkCode = `<a href="${inquireUrl}" target="_blank" rel="noopener">Request a vehicle</a>`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      const { toast } = await import("sonner");
      toast.success(`${label} copied`);
    } catch (_) {
      window.prompt("Copy:", text);
    }
  };

  return (
    <section className="mt-8 bg-zinc-50 border border-zinc-200 rounded-sm" data-testid="embed-panel">
      <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-2">
        <Code2 className="w-4 h-4 text-zinc-700" />
        <h2 className="font-heading text-lg font-bold">Put this form on your website</h2>
      </div>
      <div className="p-6 space-y-5">
        <div>
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Option A · Embed (iframe)</div>
              <p className="text-xs text-zinc-500 mt-0.5">Paste this anywhere on your site. The form renders inside the iframe.</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => copy(iframeCode, "Embed code")} data-testid="copy-iframe-btn" className="rounded-sm border-zinc-300">
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
            </Button>
          </div>
          <pre className="bg-zinc-900 text-zinc-100 text-xs font-mono p-3 rounded-sm overflow-x-auto whitespace-pre-wrap break-all" data-testid="iframe-code">{iframeCode}</pre>
        </div>
        <div>
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Option B · Direct link</div>
              <p className="text-xs text-zinc-500 mt-0.5">Or just link to it from a button on your site.</p>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => copy(inquireUrl, "URL")} data-testid="copy-url-btn" className="rounded-sm border-zinc-300">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy URL
              </Button>
              <a href={inquireUrl} target="_blank" rel="noopener noreferrer" data-testid="preview-inquire-link">
                <Button type="button" size="sm" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open form
                </Button>
              </a>
            </div>
          </div>
          <pre className="bg-zinc-900 text-zinc-100 text-xs font-mono p-3 rounded-sm overflow-x-auto whitespace-pre-wrap break-all" data-testid="link-code">{linkCode}</pre>
        </div>
        <p className="text-xs text-zinc-500">
          Submissions appear in this CRM tagged <span className="font-bold text-zinc-700">Website</span>. No login required for visitors.
        </p>
      </div>
    </section>
  );
}

export default function CRM() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCustomer);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([api.get("/customers"), api.get("/crm/matches")]);
      setCustomers(c.data);
      setMatches(m.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        desired_ymm: form.desired_ymm.trim(),
        engine_type: form.engine_type,
        transmission: form.transmission,
        steering: form.steering,
        colors: form.colors.trim(),
        time_frame: form.time_frame,
        payment: form.payment,
        notes: form.notes,
        budget_min: form.budget_min === "" ? null : Number(form.budget_min),
        budget_max: form.budget_max === "" ? null : Number(form.budget_max),
      };
      await api.post("/customers", payload);
      toast.success("Customer added");
      setOpen(false);
      setForm(emptyCustomer);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.phone || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q)
    );
  }, [customers, query]);

  const totalMatches = matches.reduce((acc, m) => acc + (m.matches?.length || 0), 0);
  const unviewedWebsite = customers.filter((c) => c.source === "website" && !c.viewed).length;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10 max-w-7xl">
      <PageHeader
        eyebrow="Customer Relationships"
        title="CRM"
        description="Track customers and the vehicles they're looking for. Get instant matches when matching inventory arrives."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-customer-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                <Plus className="w-4 h-4 mr-2" /> Add Customer
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl tracking-tight">New customer</DialogTitle>
              </DialogHeader>
              <form onSubmit={onCreate} className="space-y-4" data-testid="customer-form">
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" className="rounded-sm border-zinc-300" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Phone</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone-input" className="rounded-sm border-zinc-300 font-mono" />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email-input" className="rounded-sm border-zinc-300" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Year Make Model</Label>
                  <Input
                    value={form.desired_ymm}
                    onChange={(e) => setForm({ ...form, desired_ymm: e.target.value })}
                    placeholder="Ex 1993 Toyota Supra"
                    data-testid="customer-ymm-input"
                    className="rounded-sm border-zinc-300"
                  />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Engine</Label>
                  <Select value={form.engine_type || "none"} onValueChange={(v) => setForm({ ...form, engine_type: v === "none" ? "" : v })}>
                    <SelectTrigger className="rounded-sm border-zinc-300" data-testid="customer-engine-select">
                      <SelectValue placeholder="Select engine type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      <SelectItem value="na">NA / Non Turbo</SelectItem>
                      <SelectItem value="turbo">Turbo / Twin Turbo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Transmission</Label>
                    <Select value={form.transmission || "none"} onValueChange={(v) => setForm({ ...form, transmission: v === "none" ? "" : v })}>
                      <SelectTrigger className="rounded-sm border-zinc-300" data-testid="customer-transmission-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {TRANSMISSION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Steering</Label>
                    <Select value={form.steering || "none"} onValueChange={(v) => setForm({ ...form, steering: v === "none" ? "" : v })}>
                      <SelectTrigger className="rounded-sm border-zinc-300" data-testid="customer-steering-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {STEERING_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Colors</Label>
                  <Input value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} placeholder="e.g. White, Black, Silver" data-testid="customer-colors-input" className="rounded-sm border-zinc-300" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Time Frame</Label>
                    <Select value={form.time_frame || "none"} onValueChange={(v) => setForm({ ...form, time_frame: v === "none" ? "" : v })}>
                      <SelectTrigger className="rounded-sm border-zinc-300" data-testid="customer-timeframe-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {TIME_FRAME_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Payment</Label>
                    <Select value={form.payment || "none"} onValueChange={(v) => setForm({ ...form, payment: v === "none" ? "" : v })}>
                      <SelectTrigger className="rounded-sm border-zinc-300" data-testid="customer-payment-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {PAYMENT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Budget Min</Label>
                    <Input type="number" step="0.01" value={form.budget_min} onChange={(e) => setForm({ ...form, budget_min: e.target.value })} data-testid="customer-budget-min-input" className="rounded-sm border-zinc-300 font-mono" />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Budget Max</Label>
                    <Input type="number" step="0.01" value={form.budget_max} onChange={(e) => setForm({ ...form, budget_max: e.target.value })} data-testid="customer-budget-max-input" className="rounded-sm border-zinc-300 font-mono" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm border-zinc-300" rows={3} />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button type="submit" disabled={saving} data-testid="customer-save-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                    {saving ? "Saving..." : "Add customer"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">Customers</div>
          <div className="font-heading text-3xl font-black mt-2" data-testid="stat-customer-count">{customers.length}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">Active Requests</div>
          <div className="font-heading text-3xl font-black mt-2" data-testid="stat-active-requests">
            {customers.reduce((a, c) => a + (c.active_request_count || 0), 0)}
          </div>
        </div>
        <div className={`rounded-sm p-5 border-2 ${unviewedWebsite > 0 ? "bg-red-50 border-[#FF2400]" : "bg-white border-zinc-200"}`}>
          <div className={`text-[10px] tracking-[0.3em] uppercase font-bold ${unviewedWebsite > 0 ? "text-[#FF2400]" : "text-zinc-500"}`}>New Website Leads</div>
          <div className="font-heading text-3xl font-black mt-2" data-testid="stat-new-leads">{unviewedWebsite}</div>
        </div>
        <div className="bg-amber-50 border-2 border-amber-300 rounded-sm p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-amber-800">Live Matches</div>
          <div className="font-heading text-3xl font-black mt-2 text-amber-900" data-testid="stat-live-matches">{totalMatches}</div>
        </div>
      </div>

      {matches.length > 0 && (
        <section className="mt-8 bg-white border border-zinc-200 rounded-sm" data-testid="matches-section">
          <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-600" />
            <h2 className="font-heading text-lg font-bold">Open matches</h2>
            <span className="text-xs font-mono text-zinc-500 ml-2">({matches.length} request{matches.length > 1 ? "s" : ""})</span>
          </div>
          <ul className="divide-y divide-zinc-200">
            {matches.map((m, idx) => (
              <li key={`${m.customer.id}-${m.request.id}-${idx}`} className="px-6 py-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div>
                    <Link to={`/crm/${m.customer.id}`} className="font-bold text-zinc-950 hover:text-[#002FA7] hover:underline" data-testid={`match-customer-${m.customer.id}`}>
                      {m.customer.name}
                    </Link>
                    <span className="text-zinc-400 mx-2">·</span>
                    <span className="text-sm text-zinc-700">
                      {m.request.make} {m.request.model}
                      {m.request.year_min || m.request.year_max ? (
                        <span className="font-mono"> · {m.request.year_min || "any"}–{m.request.year_max || "any"}</span>
                      ) : null}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {m.matches.map((v) => (
                      <Link key={v.id} to={`/vehicles/${v.id}`} className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-sm text-xs font-bold text-amber-900 hover:bg-amber-200" data-testid={`match-vehicle-${v.id}`}>
                        <span>{v.year} {v.make} {v.model}</span>
                        <span className="font-mono text-amber-800">{v.sale_price != null ? currency(v.sale_price) : currency(v.purchase_price)}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <EmbedPanel />

      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-bold">All customers</h2>
          <Input
            placeholder="Search name, phone, email..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="customer-search-input"
            className="max-w-xs rounded-sm border-zinc-300"
          />
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                {["Name", "Contact", "Year Make Model", "Engine", "Budget", "Requests", "Added", "Matches", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {loading && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-zinc-500">Loading...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-zinc-500">
                  <UsersIcon className="w-6 h-6 mx-auto mb-2 text-zinc-300" />
                  {customers.length === 0 ? 'No customers yet. Click "Add Customer" to start tracking buyer interest.' : "No matches for that search."}
                </td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className={`hover:bg-zinc-50 ${c.source === "website" && !c.viewed ? "bg-red-50/40" : ""}`} data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/crm/${c.id}`} className="font-semibold text-zinc-950 hover:text-[#002FA7] hover:underline" data-testid={`open-customer-${c.id}`}>
                      {c.name}
                    </Link>
                    {c.source === "website" && !c.viewed && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] tracking-[0.15em] uppercase font-bold rounded-sm bg-[#FF2400] text-white" data-testid={`new-lead-badge-${c.id}`} title="New website lead — not viewed yet">
                        New
                      </span>
                    )}
                    {c.source === "website" && c.viewed && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] tracking-[0.15em] uppercase font-bold rounded-sm border bg-violet-50 text-violet-700 border-violet-200" title="Submitted via public form">
                        Website
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-zinc-700">
                    <div className="space-y-0.5">
                      {c.phone && <div className="font-mono inline-flex items-center gap-1.5"><Phone className="w-3 h-3 text-zinc-400" />{c.phone}</div>}
                      {c.email && <div className="inline-flex items-center gap-1.5"><Mail className="w-3 h-3 text-zinc-400" />{c.email}</div>}
                      {!c.phone && !c.email && <span className="text-zinc-300">—</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-zinc-800">{c.desired_ymm || <span className="text-zinc-300">—</span>}</td>
                  <td className="px-4 py-3 text-xs">
                    {c.engine_type === "na" && <span className="inline-block px-2 py-0.5 rounded-sm border bg-sky-50 text-sky-800 border-sky-200 font-bold tracking-[0.1em] uppercase">NA</span>}
                    {c.engine_type === "turbo" && <span className="inline-block px-2 py-0.5 rounded-sm border bg-rose-50 text-rose-800 border-rose-200 font-bold tracking-[0.1em] uppercase">Turbo</span>}
                    {!c.engine_type && <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-zinc-700">
                    {c.budget_min == null && c.budget_max == null ? "—" : `${c.budget_min != null ? currency(c.budget_min) : "any"} – ${c.budget_max != null ? currency(c.budget_max) : "any"}`}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-zinc-700">
                    {c.active_request_count || 0} active / {c.request_count || 0} total
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-zinc-700" data-testid={`customer-added-${c.id}`}>
                    {c.created_at
                      ? new Date(c.created_at).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })
                      : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {c.match_count > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-bold rounded-sm border bg-amber-50 text-amber-800 border-amber-300">
                        <Sparkles className="w-3 h-3" /> {c.match_count} match{c.match_count > 1 ? "es" : ""}
                      </span>
                    ) : (
                      <span className="text-zinc-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/crm/${c.id}`} className="text-xs font-bold uppercase tracking-[0.15em] text-[#002FA7] hover:underline">View →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
